import React, {useState, useEffect} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Box,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Avatar,
} from '@mui/material';
import {
  History,
  Restore,
  Visibility,
  Event,
  Person,
  Description,
} from '@mui/icons-material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import {Block, Confirm} from 'notiflix';
import {format} from 'date-fns';
import sectionContentApi from 'api/sectionContentApi';

function SectionHistoryModal({open, onClose, sectionId, onRestore}) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);

  useEffect(() => {
    if (open && sectionId) {
      fetchVersions();
    }
  }, [open, sectionId]);

  const fetchVersions = async () => {
    try {
      setLoading(true);
      Block.circle('#history-loading', 'Loading history...');
      const response = await sectionContentApi.getSectionVersions(sectionId);
      setVersions(response.versions || []);
    } catch (error) {
      console.error('Error fetching versions:', error);
    } finally {
      setLoading(false);
      Block.remove('#history-loading');
    }
  };

  const handleRestore = version => {
    Confirm.show(
      'Restore Version',
      `Are you sure you want to restore version ${version.versionNumber}? Current changes will be saved as a new version.`,
      'Yes, Restore',
      'Cancel',
      async () => {
        try {
          Block.circle('#restore-loading', 'Restoring...');
          await onRestore(version.id);
          onClose();
        } catch (error) {
          console.error('Restore error:', error);
        } finally {
          Block.remove('#restore-loading');
        }
      },
    );
  };

  const handlePreview = version => {
    // Preview logic for specific version
    setSelectedVersion(version);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <History color="primary" />
          <MDTypography variant="h6">Section Version History</MDTypography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box id="history-loading" textAlign="center" py={4}>
            <MDTypography>Loading history...</MDTypography>
          </Box>
        ) : versions.length === 0 ? (
          <Box textAlign="center" py={4}>
            <MDTypography color="text">
              No version history available
            </MDTypography>
          </Box>
        ) : (
          <List>
            {versions.map((version, index) => (
              <React.Fragment key={version.id}>
                <ListItem
                  sx={{
                    bgcolor: version.isCurrent
                      ? 'action.selected'
                      : 'transparent',
                    borderRadius: 1,
                    mb: 1,
                  }}
                  secondaryAction={
                    <Box>
                      <Tooltip title="Preview this version">
                        <IconButton
                          onClick={() => handlePreview(version)}
                          size="small">
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      {!version.isCurrent && (
                        <Tooltip title="Restore this version">
                          <IconButton
                            onClick={() => handleRestore(version)}
                            size="small"
                            color="primary">
                            <Restore />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  }>
                  <ListItemIcon>
                    <Avatar
                      sx={{
                        bgcolor: version.isCurrent
                          ? 'success.main'
                          : 'grey.500',
                        width: 32,
                        height: 32,
                      }}>
                      {version.versionNumber}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <MDTypography variant="body2" fontWeight="bold">
                          Version {version.versionNumber}
                        </MDTypography>
                        {version.isCurrent && (
                          <Chip label="Current" size="small" color="success" />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={2}
                          mt={0.5}>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Event fontSize="small" color="action" />
                            <MDTypography variant="caption">
                              {format(
                                new Date(version.createdAt),
                                'MMM dd, yyyy HH:mm',
                              )}
                            </MDTypography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Person fontSize="small" color="action" />
                            <MDTypography variant="caption">
                              {version.createdBy?.name || 'System'}
                            </MDTypography>
                          </Box>
                        </Box>
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={0.5}
                          mt={0.5}>
                          <Description fontSize="small" color="action" />
                          <MDTypography variant="caption" color="text">
                            {version.changeSummary || 'No change description'}
                          </MDTypography>
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
                {index < versions.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Preview Dialog for Version */}
      <Dialog
        open={Boolean(selectedVersion)}
        onClose={() => setSelectedVersion(null)}
        maxWidth="md"
        fullWidth>
        <DialogTitle>
          Version {selectedVersion?.versionNumber} Preview
        </DialogTitle>
        <DialogContent>
          <pre style={{maxHeight: 400, overflow: 'auto'}}>
            {JSON.stringify(selectedVersion?.content, null, 2)}
          </pre>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedVersion(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

export default SectionHistoryModal;
